import { memo } from 'react';
import { Input, Label } from '@flote/ui';
import { Search } from 'lucide-react';
import type { AddressState } from '../hooks';

interface AddressInputProps {
  address: AddressState;
  onAddressChange: (field: keyof AddressState, value: string) => void;
  inputRef?: (node: HTMLInputElement | null) => void;
  errors?: Record<string, string>;
  errorPrefix?: string;
  disabled?: boolean;
  searchLabel?: string;
  searchPlaceholder?: string;
}

export const AddressInput = memo<AddressInputProps>(({
  address,
  onAddressChange,
  inputRef,
  errors = {},
  errorPrefix = '',
  disabled = false,
  searchLabel = 'Search Address',
  searchPlaceholder = 'Start typing to search for an address...',
}) => {
  const getError = (field: string) => errors[`${errorPrefix}${field}`];

  return (
    <div className="space-y-4">
      {/* Address Search with Autocomplete */}
      <div className="space-y-2">
        <Label htmlFor={`${errorPrefix}addressSearch`}>{searchLabel}</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            id={`${errorPrefix}addressSearch`}
            placeholder={searchPlaceholder}
            value={address.search}
            onChange={(e) => onAddressChange('search', e.target.value)}
            disabled={disabled}
            className="pl-9"
            autoComplete="off"
          />
        </div>
      </div>

      {/* Parsed Address Fields - Row 1 */}
      <div className="grid gap-4 grid-cols-2 sm:grid-cols-4">
        <div className="space-y-2">
          <Label htmlFor={`${errorPrefix}streetNumber`}>Street Number *</Label>
          <Input
            id={`${errorPrefix}streetNumber`}
            placeholder="123"
            value={address.streetNumber}
            onChange={(e) => onAddressChange('streetNumber', e.target.value)}
            disabled={disabled}
            className={getError('streetNumber') ? 'border-destructive' : ''}
          />
          {getError('streetNumber') && (
            <p className="text-xs text-destructive">{getError('streetNumber')}</p>
          )}
        </div>

        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor={`${errorPrefix}streetAddress`}>Street Name *</Label>
          <Input
            id={`${errorPrefix}streetAddress`}
            placeholder="Main Street"
            value={address.streetAddress}
            onChange={(e) => onAddressChange('streetAddress', e.target.value)}
            disabled={disabled}
            className={getError('streetAddress') ? 'border-destructive' : ''}
          />
          {getError('streetAddress') && (
            <p className="text-xs text-destructive">{getError('streetAddress')}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${errorPrefix}apartment`}>Apt / Suite</Label>
          <Input
            id={`${errorPrefix}apartment`}
            placeholder="Unit 4B"
            value={address.apartment}
            onChange={(e) => onAddressChange('apartment', e.target.value)}
            disabled={disabled}
          />
        </div>
      </div>

      {/* Parsed Address Fields - Row 2 */}
      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor={`${errorPrefix}city`}>City *</Label>
          <Input
            id={`${errorPrefix}city`}
            placeholder="Los Angeles"
            value={address.city}
            onChange={(e) => onAddressChange('city', e.target.value)}
            disabled={disabled}
            className={getError('city') ? 'border-destructive' : ''}
          />
          {getError('city') && (
            <p className="text-xs text-destructive">{getError('city')}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${errorPrefix}state`}>State</Label>
          <Input
            id={`${errorPrefix}state`}
            placeholder="CA"
            value={address.state}
            onChange={(e) => onAddressChange('state', e.target.value.toUpperCase())}
            disabled={disabled}
            maxLength={2}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${errorPrefix}postalCode`}>Postal Code</Label>
          <Input
            id={`${errorPrefix}postalCode`}
            placeholder="90001"
            value={address.postalCode}
            onChange={(e) => onAddressChange('postalCode', e.target.value)}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
});

AddressInput.displayName = 'AddressInput';
